import type { SxProps } from '@mui/material';
import { useState } from 'react';

import { PanelWrapper } from '@/components/panels/panelWrapper';
import { useViewParameters } from '@/contexts/navigation';
import { useWorkspace } from '@/contexts/useWorkspace';

import type { Step } from './Step';

interface WizardProps<T> {
  sx?: SxProps;
  onFinish: (state: T) => Promise<boolean>;
  onAbort: (origin?: 'back') => void;
  title?: string;
  subtitle?: React.ReactNode;
  hideBorder?: boolean;
  initialState: T;
  flow: Step<T>[];
  stepperLabels?: string[];
  hideBackArrow?: boolean;
  hideCloseButton?: boolean;
  storageKey?: string;
  initialStep?: {
    stepIndex: number;
    keepPreviousSteps?: boolean;
  };
}

const getInitialStep = <T,>(
  flow: Step<T>[],
  initialStepConfig?: { stepIndex: number; keepPreviousSteps?: boolean }
): Step<T>[] => {
  let initialStep: Step<T>[] = flow;
  let currentIndex = initialStepConfig?.stepIndex || 0;

  while (currentIndex > 0) {
    const nextSteps: Step<T>[] = initialStep.at(-1)?.next || [];
    const currentStep: Step<T>[] = initialStepConfig?.keepPreviousSteps
      ? initialStep
      : [];

    initialStep = [...currentStep, ...nextSteps];
    currentIndex--;
  }
  return initialStep;
};

export const Wizard = <T,>(props: WizardProps<T>) => {
  const [steps, setSteps] = useState(
    getInitialStep(props.flow, props.initialStep)
  );
  const currentStep = steps[steps.length - 1];

  const [wizardState, setWizardState] = useState(props.initialState);

  const { rightPanel, snackbar } = useWorkspace();
  const [finishInProgress, setFinishInProgress] = useState(false);
  const { removeParam } = useViewParameters();

  const back = () => {
    if (steps.length <= 1) {
      props.onAbort('back');
    } else {
      setSteps(steps.slice(0, steps.length - 1));
    }
  };

  const next = (step: Step<T> | undefined) => {
    if (step) {
      if (currentStep.nonReturnable) {
        setSteps([...steps.slice(0, -1), step]);
      } else {
        setSteps([...steps, step]);
      }
    }
  };

  const finish = async (successMessage?: string) => {
    if (finishInProgress) {
      return;
    }

    setFinishInProgress(true);
    try {
      const finishedSuccessfully = await props.onFinish(wizardState);
      if (finishedSuccessfully) {
        if (successMessage) {
          snackbar.showMessage(successMessage);
        }
        rightPanel.setIsOpen(false);
        removeParam('id');
      }
    } catch (error) {
      window.reportError(error);
    } finally {
      setFinishInProgress(false);
    }
  };

  return (
    <PanelWrapper
      sx={props.sx}
      testIdPrefix='wizard'
      header={
        props.title || !props.hideBackArrow
          ? {
              hideBorder: props.hideBorder ?? true,
              title: props.title,
              subtitle: props.subtitle,
              onBack: props.hideBackArrow ? undefined : back,
              onClose: props.hideCloseButton ? undefined : props.onAbort,
            }
          : undefined
      }
    >
      <currentStep.Component
        stepperLabels={props.stepperLabels ?? []}
        currentStep={currentStep}
        back={back}
        wizardState={wizardState}
        initialState={props.initialState}
        setWizardState={setWizardState}
        finish={finish}
        setNextStep={next}
      />
    </PanelWrapper>
  );
};
